import {
  faUserCircle,
  faExclamationTriangle,
  faFileAlt,
  faUpload,
  faCalendar,
  faClipboardList,
  faCheckCircle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { DEFAULT_LETTER_TYPE_CODE, todayCalendarDate, formatCodeWithCode } from '@medprompt/shared';

export const WorkflowConditionEnum = {
  ASSIGNEE_EQUALS: {
    value: 'ASSIGNEE_EQUALS',
    label: 'assigned to',
    paramKey: 'assigneeId',
    hasParameters: true,
  },
  RISK_CATEGORY_EQUALS: {
    value: 'RISK_CATEGORY_EQUALS',
    label: 'urgency is',
    paramKey: 'riskCategory',
    hasParameters: true,
  },
  LETTER_TYPE_EQUALS: {
    value: 'LETTER_TYPE_EQUALS',
    label: 'letter type is',
    paramKey: 'letterTypeCode',
    hasParameters: true,
  },
  SUMMARY_LIKE: {
    value: 'SUMMARY_LIKE',
    label: 'summary matches',
    paramKey: 'query',
    hasParameters: true,
  },
  HAS_OUTCOME_TASKS: {
    value: 'HAS_OUTCOME_TASKS',
    label: 'has outcome tasks',
    hasParameters: false,
  },
  HAS_NO_OUTCOME_TASKS: {
    value: 'HAS_NO_OUTCOME_TASKS',
    label: 'has no outcome tasks',
    hasParameters: false,
  },
  IS_SYSTEM_UPLOAD: {
    value: 'IS_SYSTEM_UPLOAD',
    label: 'uploaded by system',
    hasParameters: false,
  },
  IS_USER_UPLOAD: {
    value: 'IS_USER_UPLOAD',
    label: 'uploaded by user',
    hasParameters: false,
  },
  IS_DAY_OF_WEEK: {
    value: 'IS_DAY_OF_WEEK',
    label: 'day of week is',
    paramKey: 'dayOfWeek',
    hasParameters: true,
  },
  IS_WEEKDAY: {
    value: 'IS_WEEKDAY',
    label: "it's a weekday",
    hasParameters: false,
  },
  IS_WEEKEND: {
    value: 'IS_WEEKEND',
    label: "it's a weekend",
    hasParameters: false,
  },
  DATE_EQUALS: {
    value: 'DATE_EQUALS',
    label: 'date is',
    paramKey: 'date',
    hasParameters: true,
  },
};

export const WORKFLOW_CONDITION_TYPE_OPTIONS = Object.values(WorkflowConditionEnum).map(
  ({ value, label }) => ({ value, name: label })
);

export const DAY_OF_WEEK_OPTIONS = [
  { value: 'MONDAY', name: 'Monday' },
  { value: 'TUESDAY', name: 'Tuesday' },
  { value: 'WEDNESDAY', name: 'Wednesday' },
  { value: 'THURSDAY', name: 'Thursday' },
  { value: 'FRIDAY', name: 'Friday' },
  { value: 'SATURDAY', name: 'Saturday' },
  { value: 'SUNDAY', name: 'Sunday' },
];

export const DEFAULT_NEW_CONDITION = { type: WorkflowConditionEnum.IS_WEEKDAY.value };

export function createDefaultCondition(type) {
  switch (type) {
    case WorkflowConditionEnum.ASSIGNEE_EQUALS.value:
      return { type, assigneeId: null };
    case WorkflowConditionEnum.RISK_CATEGORY_EQUALS.value:
      return { type, riskCategory: 'HIGH' };
    case WorkflowConditionEnum.LETTER_TYPE_EQUALS.value:
      return { type, letterTypeCode: { ...DEFAULT_LETTER_TYPE_CODE } };
    case WorkflowConditionEnum.SUMMARY_LIKE.value:
      return { type, query: '' };
    case WorkflowConditionEnum.IS_DAY_OF_WEEK.value:
      return { type, dayOfWeek: 'MONDAY' };
    case WorkflowConditionEnum.DATE_EQUALS.value:
      return { type, date: todayCalendarDate() };
    default:
      return { type };
  }
}

export function getWorkflowConditionParamKey(type) {
  return WorkflowConditionEnum[type]?.paramKey ?? null;
}

/** @returns {string|null} validation error message, or null if valid */
export function getWorkflowConditionsValidationError(conditions) {
  const blankSummaryQuery = conditions?.some(
    (c) => c.type === WorkflowConditionEnum.SUMMARY_LIKE.value && !c.query?.trim()
  );
  if (blankSummaryQuery) {
    return 'Summary match query is required';
  }
  return null;
}

// Exported so WorkflowDiagram can reuse for action formatters too
export function getAssigneeName(assigneeId, activeUsers, teams) {
  if (!assigneeId || assigneeId === '*') return 'Unassigned';
  const user = activeUsers?.find((u) => u.userId === assigneeId);
  if (user) return user.name;
  const team = teams?.find((t) => t.teamId === assigneeId);
  if (team) return team.teamName;
  return assigneeId;
}

export function formatWorkflowConditionText(condition, activeUsers, teams) {
  switch (condition.type) {
    case WorkflowConditionEnum.ASSIGNEE_EQUALS.value:
      return `document is assigned to ${getAssigneeName(condition.assigneeId, activeUsers, teams)}`;
    case WorkflowConditionEnum.RISK_CATEGORY_EQUALS.value:
      return `urgency is ${condition.riskCategory}`;
    case WorkflowConditionEnum.LETTER_TYPE_EQUALS.value:
      return `letter type is ${formatCodeWithCode(condition.letterTypeCode)}`;
    case WorkflowConditionEnum.SUMMARY_LIKE.value: {
      const query = condition.query || '';
      return query ? `summary matches "${query}"` : 'summary matches query';
    }
    case WorkflowConditionEnum.IS_SYSTEM_UPLOAD.value:
      return 'document was uploaded by the system';
    case WorkflowConditionEnum.IS_USER_UPLOAD.value:
      return 'document was uploaded by a user';
    case WorkflowConditionEnum.IS_DAY_OF_WEEK.value:
      return `today is ${condition.dayOfWeek}`;
    case WorkflowConditionEnum.IS_WEEKDAY.value:
      return 'today is a weekday';
    case WorkflowConditionEnum.IS_WEEKEND.value:
      return 'today is a weekend';
    case WorkflowConditionEnum.DATE_EQUALS.value:
      return `today's date is ${condition.date}`;
    case WorkflowConditionEnum.HAS_OUTCOME_TASKS.value:
      return 'document has outcome tasks';
    case WorkflowConditionEnum.HAS_NO_OUTCOME_TASKS.value:
      return 'document has no outcome tasks';
    default:
      return condition.type;
  }
}

export function getWorkflowConditionIcon(condition) {
  switch (condition.type) {
    case WorkflowConditionEnum.ASSIGNEE_EQUALS.value:
      return faUserCircle;
    case WorkflowConditionEnum.RISK_CATEGORY_EQUALS.value:
      return faExclamationTriangle;
    case WorkflowConditionEnum.LETTER_TYPE_EQUALS.value:
      return faFileAlt;
    case WorkflowConditionEnum.SUMMARY_LIKE.value:
      return faSearch;
    case WorkflowConditionEnum.IS_SYSTEM_UPLOAD.value:
    case WorkflowConditionEnum.IS_USER_UPLOAD.value:
      return faUpload;
    case WorkflowConditionEnum.IS_DAY_OF_WEEK.value:
    case WorkflowConditionEnum.IS_WEEKDAY.value:
    case WorkflowConditionEnum.IS_WEEKEND.value:
    case WorkflowConditionEnum.DATE_EQUALS.value:
      return faCalendar;
    case WorkflowConditionEnum.HAS_OUTCOME_TASKS.value:
    case WorkflowConditionEnum.HAS_NO_OUTCOME_TASKS.value:
      return faClipboardList;
    default:
      return faCheckCircle;
  }
}
